import Hls from 'hls.js';
import { FC, useEffect, useRef, useState } from 'react';
// import styles from '../Videos/VideoItem/VideoItem.module.scss';
import { useMediaQuery } from '@mui/material';
import { Circle } from 'src/api/circles';
import dunbarWhite from 'src/assets/images/dunbar/dunbar-white.png';
import { useVideoEngagement } from 'src/utils/videoEngagement';
import styles from './WelcomeVideo.module.scss';

interface WelcomeVideoProps {
  circleData: Circle | null;
}

export const WelcomeVideo: FC<WelcomeVideoProps> = ({ circleData }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isMobile = useMediaQuery('(max-width:545px)');
  const [isLoading, setIsLoading] = useState(true);
  const [currentPlayingVideo, setCurrentPlayingVideo] =
    useState<HTMLVideoElement | null>(null);
  const welcomeVideoUrl = circleData?.welcome_video.url ?? '';

  const { createHandlers } = useVideoEngagement({
    postId: circleData?._id ?? '',
  });

  const handleVideoClick = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  useEffect(() => {
    let hls: Hls | undefined;
    const video = videoRef.current;

    const handleCanPlay = () => setIsLoading(false);
    const handleWaiting = () => setIsLoading(true);
    const handleLoadedData = () => setIsLoading(false);

    if (!video || !welcomeVideoUrl) {
      return;
    }

    video.oncanplay = handleCanPlay;
    video.onwaiting = handleWaiting;
    video.onloadeddata = handleLoadedData;

    if (Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(welcomeVideoUrl);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = welcomeVideoUrl;
    }

    return () => {
      if (video) {
        video.oncanplay = null;
        video.onwaiting = null;
        video.onloadeddata = null;
      }
      hls?.destroy();
    };
  }, [welcomeVideoUrl]);

  // Set up video engagement handlers
  useEffect(() => {
    if (circleData && videoRef.current) {
      const { handleTimeUpdate, handleEnded } = createHandlers(videoRef);
      const video = videoRef.current;

      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('ended', handleEnded);

      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('ended', handleEnded);
      };
    }
  }, [circleData, createHandlers]);

  const handleMouseEnter = () => {
    if (!isMobile && videoRef.current) {
      if (currentPlayingVideo && currentPlayingVideo !== videoRef.current) {
        currentPlayingVideo.pause();
      }

      videoRef.current.play();
      setCurrentPlayingVideo(videoRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile && videoRef.current) {
      videoRef.current.pause();
    }
  };

  const calculateRelativeTime = (date: string) => {
    const createdAt = new Date(date);

    const formattedDate = `${createdAt.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
    })}, ${createdAt.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })}`;

    return formattedDate;
  };

  const relativeTime = calculateRelativeTime(circleData?.createdAt ?? '');

  return (
    <div
      className={styles.videoContainer}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.videoWrapper}>
        {isLoading && welcomeVideoUrl && (
          <div className={styles.loaderOverlay}>
            <div className={styles.loaderSpinner}></div>
          </div>
        )}

        <video
          ref={videoRef}
          className={styles.video}
          playsInline
          onClick={handleVideoClick}
        >
          Your browser does not support the video tag.
        </video>
      </div>
      <div className={styles.challengeOverlay}>
        <div className={styles.topData}>
          <div className={styles.circleName}>{circleData?.tribe_name}</div>
          <div className={styles.time}>{relativeTime}</div>
        </div>
        <div className={styles.dunbarIconWrapper}>
          <img src={dunbarWhite} alt="Dunbar" className={styles.dunbarIcon} />
        </div>
        <div className={styles.commentsCount}>
          {/* {item.comments.length} comments */}
        </div>
      </div>
    </div>
  );
};
